import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useT } from "../i18n";
import { istInstalliert, useTelefon } from "../schale/geraet";
import { ohneNetz, uebertragen, useWarteschlange } from "../daten/warteschlange";
import "../stile/ziehauffrischen.css";
import { uhrImBetrieb } from "../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Runterziehen zum Aktualisieren.

   Luca, 17.09.2026: *„Agent, der sich darum kümmert, dass in der
   Mobile-PWA auch die Möglichkeit besteht, runterzuziehen, um zu
   aktualisieren."*

   Es ist die eine Geste, die am Telefon niemand lernen muss. Wer auf dem
   Hof steht und wissen will, ob sich am Tag etwas geändert hat, zieht —
   und bis heute passierte nichts. In der installierten PWA gibt es
   daneben **keinen** anderen Weg: keine Adresszeile, kein Neuladen-Pfeil,
   kein Wischen von links. Ein Schirm, der nicht von selbst nachlädt, ist
   dort ein Schirm von vorhin, und niemand sieht ihm das an.

   ── Aktualisieren heißt hier zweierlei ────────────────────────────

   1. **Was auf dem Gerät liegt, geht hinaus** (`daten/warteschlange.ts`).
      Wer im Funkloch ein Protokoll aufgenommen hat und wieder Empfang
      hat, zieht genau dafür. Das ist der erste Schritt und nicht der
      zweite: Erst schieben, dann holen — sonst holt man einen Stand, in
      dem die eigene Übergabe noch fehlt.
   2. **Was der Server jetzt sagt, wird geholt** —
      `refetchQueries({ type: "active" })`. „Aktiv" ist genau, was diese
      Maske gerade zeigt; eine Liste von Schlüsseln je Maske wäre
      dieselbe Liste noch einmal, und die dreizehnte Maske stünde nicht
      darin.

   ── Ein Baustein, kein Einbau je Maske ────────────────────────────

   Die Geste sitzt **einmal** in der Schale (`schale/Schale.tsx`) und
   horcht am Arbeitsbereich `#maskenraum`. Eine Maske hängt sich mit
   einer Zeile ein:

   ```tsx
   useZiehauffrischen();
   ```

   Mehr braucht sie nicht — was zu holen ist, weiß TanStack Query
   ohnehin. Wer darüber hinaus etwas tun muss, das keine Abfrage ist,
   gibt es mit: `useZiehauffrischen(() => neuLesen())`.

   **Ohne diese Zeile gibt es die Geste nicht.** Das ist Absicht: Eine
   reine Formularmaske — Preisauskunft, Angebot schreiben — hat nichts
   zu holen, und ein Kreisel über einem halb ausgefüllten Formular
   verspricht etwas, das nicht passiert.

   ── Nur am Telefon, nur installiert ───────────────────────────────

   `schale/geraet.ts` ist die eine Stelle, die sagt, worauf das hier
   läuft. Am Schreibtisch existiert die Geste **gar nicht** — dort gibt
   es F5. Im Telefonbrowser ebenfalls nicht: Chrome und Safari bringen
   ihr eigenes Überziehen mit, und zwei Kreisel übereinander sind einer
   zu viel. Übrig bleibt die installierte PWA — genau der Fall, in dem
   sonst nichts da ist.

   ── Warum Berührungsereignisse und nicht Zeigerereignisse ─────────

   `Blattvonunten.tsx` zieht mit Pointer Events, und das ist dort
   richtig: Das Blatt liegt über allem, niemand rollt darunter. Hier
   liegt die Geste **auf** einer Liste, die selbst rollt — und die
   Bewegung muss dem Browser abgenommen werden, bevor er sein eigenes
   Gummiband spannt. Das geht nur mit `touchmove` und `preventDefault()`,
   und dafür muss der Horcher `passive: false` sein. Zeigerereignisse
   können das nicht.

   ── Was der Geste nicht gehört ────────────────────────────────────

   * **Ein offenes Blatt.** `.bvu-schirm` und `.deck-schirm` liegen mit
     `inset: 0` über dem Arbeitsbereich; wer dort zieht, meint das Blatt
     (`Blattvonunten.tsx` hat seine eigene Ziehgeste, samt Wegwischen).
     Geprüft wird beides: ob die Berührung **darin** beginnt und ob
     überhaupt eines offen steht.
   * **Eine Liste, die nicht oben steht.** Gezogen wird erst, wenn der
     Roller unter dem Finger auf `scrollTop === 0` steht — sonst rollt
     er weiter, wie er soll.
   * **Eine Bewegung zur Seite.** Wer quer wischt, meint nicht dies.
   * **Zwei Finger.** Das ist Zoom.
   * **Die untere Leiste und die schwebenden Knöpfe** (seit 19.09.2026,
     `NICHT_HIER` unten). Sie rollen nicht; wer dort nach unten abrollt,
     tippt.

   **Knöpfe sind ausdrücklich nicht ausgenommen.** Am Telefon ist jede
   Zeile ein `<button>` (`bausteine/Handyzeile.tsx`) — wer Knöpfe
   aussparte, sparte die ganze Liste aus. Dass aus dem Zug kein Tipper
   wird, erledigt der Browser: Ab etwa zehn Pixeln Weg fällt der Klick
   aus.

   ── Was das Betriebssystem beisteuert ─────────────────────────────

   `overscroll-behavior-y: contain` steht in `stile/ziehauffrischen.css`
   auf den beiden rollenden Flächen. Es hält die Bewegung in der Liste,
   statt sie an den Browser weiterzureichen — damit fällt sein eigenes
   Überziehen weg, und das gewöhnliche Rollen bleibt, wie es war.
   `touch-action` wird bewusst **nicht** gesetzt: `pan-y` auf `.handy`
   nähme zwei Finger das Zoomen und einer Tabelle daneben das Rollen zur
   Seite. Dort, wo es hingehört — am Griff eines Blatts —, steht es
   längst (`blattvonunten.css`).

   Klassen: `zauf-*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/** Der Arbeitsbereich der Schale — dieselbe Stelle wie beim Blatt von unten. */
const RAUM = "maskenraum";

/** Was über dem Arbeitsbereich liegt und die Geste für sich behält. */
const DECKT = ".bvu-schirm, .deck-schirm";

/* ── Was nie gezogen wird (19.09.2026) ──

   Luca: *„Das ist auch nicht immer zuverlässig in der Bottom-Navbar."*

   **Die untere Leiste liegt im Arbeitsbereich** (`schale/Handyleiste.tsx`) und
   damit bis heute auch im Revier dieser Geste. Gemessen am Telefon: Ein Tipp
   auf „Start", bei dem der Daumen 20 px nach unten rollt — so tippt ein Daumen
   an der Unterkante —, begann das Herunterziehen (`zauf-zieht`) und war kein
   Klick mehr. Die Leiste steht fest und rollt nicht; von ihr aus gibt es
   nichts zu ziehen. Dasselbe gilt für die Knöpfe, die über der Liste
   schweben. */
const NICHT_HIER = ".hleiste, .mlf-knopf";

/**
 * Ab hier gilt es als Zug und nicht als Tipper.
 *
 * **12 px, nicht mehr 8** (19.09.2026). Das Blatt von unten nimmt 8 — dort
 * zieht man am Griff, und wer den Griff anfasst, will ziehen. Hier liegt die
 * Geste auf **jeder** Zeile einer Liste, die oben steht, und die meisten
 * Berührungen dort sind Tipps. Ein Daumen rollt beim Tippen um einige Pixel
 * ab; iOS lässt einem Tipp rund 10 pt, bevor es ihn als Bewegung liest. Mit 8
 * begann das Ziehen schon *innerhalb* eines Tipps: `preventDefault` auf der
 * Bewegung, die Liste ruckte um einen Pixel, und der Klick war Glückssache.
 * Ab 12 ist aus dem Tipp schon eine Bewegung geworden, bevor das Ziehen ihn
 * übernimmt — der Weg bis zum Auslösen (64 px) ändert sich dadurch nicht
 * spürbar.
 */
const SCHWELLE = 12;

/** Die Höhe des Bandes. Ab hier steht es ganz im Bild. */
const HOEHE = 58;

/**
 * Wo das Band steht, wenn `gezeigt` Pixel davon zu sehen sein sollen.
 *
 * **Ganz weg heißt: auch unter der Uhr weg** (19.09.2026). Das Band hängt mit
 * `top: env(safe-area-inset-top)` unter der Statusleiste. In Ruhe stand es um
 * seine eigene Höhe darüber, also bei −11 … 47 px — und damit **im Streifen
 * der Statusleiste**, die in der installierten Anwendung durchscheint
 * (`black-translucent`). Auf dem Schirmfoto mit den Maßen des iPhones stand
 * „ZUM AKTUALISIEREN ZIEHEN" dauerhaft neben der Uhr. In Ruhe rückt es deshalb
 * um den Einsatz weiter nach oben; beim Ziehen schmilzt dieser Zuschlag mit
 * dem Weg, sodass es bei vollem Band genau dort steht, wo es stand.
 */
function bandLage(gezeigt: number): string {
  const rest = Math.max(0, Math.min(1, 1 - gezeigt / HOEHE));
  return `translateY(calc(${gezeigt - HOEHE}px - ${rest.toFixed(3)} * var(--sicher-oben)))`;
}

/** Ab diesem gezeigten Weg holt das Loslassen. Etwas mehr als die Höhe. */
const AUSLOESEN = 64;

/** Weiter als das geht es nicht — der Zug läuft asymptotisch dagegen. */
const HOECHSTENS = 140;

/** So lange bleibt das Ergebnis stehen, bevor das Band wieder hochfährt. */
const NACHKLANG = 2400;

/** So lange dauert es mindestens. Ein Kreisel, der aufblitzt, ist keiner. */
const MINDESTENS = 450;

/**
 * Der Widerstand: Der erste Zentimeter folgt fast eins zu eins, danach
 * wird es zäh, und bei `HOECHSTENS` ist Schluss. Ein Band, das dem Finger
 * über den halben Schirm folgt, sieht aus, als risse die Maske ab.
 */
function widerstand(zug: number): number {
  return HOECHSTENS * (1 - Math.exp(-zug / HOECHSTENS));
}

/** `07:12` in deutscher Zeit — dieselbe Schreibweise wie in `Offline.tsx`. */
function uhr(wann: number): string {
  return uhrImBetrieb(new Date(wann));
}

/* ══════════════ Wer eingehängt ist ══════════════

   Ein Halter und nicht eine Liste: Es steht immer genau **eine** Maske im
   `Outlet`. Hängen sich zwei ein — eine Maske, die eine andere einsetzt —,
   gewinnt die äußere, und beide meinen dasselbe. */

type Halter = { current: (() => unknown) | undefined };

let eingehaengt: Halter | null = null;
const horcher = new Set<() => void>();

function melden(): void {
  for (const h of horcher) h();
}

function abonnieren(h: () => void): () => void {
  horcher.add(h);
  return () => { horcher.delete(h); };
}

const standJetzt = (): Halter | null => eingehaengt;
const amServer = (): Halter | null => null;

/**
 * **Die eine Zeile in der Maske.** Solange sie steht, gilt am Telefon die
 * Geste; verlässt man die Maske, ist sie wieder weg.
 *
 * `eigenes` ist der Ausnahmefall — etwas, das keine Abfrage ist und
 * trotzdem mitgeholt werden muss. Es wird in einem Halter geführt und
 * nicht in der Abhängigkeitsliste: Sonst hängte sich eine Maske, die ihre
 * Funktion beim Zeichnen neu bildet, bei jedem Bild neu ein.
 */
export function useZiehauffrischen(eigenes?: () => unknown): void {
  const halter = useRef<(() => unknown) | undefined>(eigenes);
  useEffect(() => { halter.current = eigenes; });
  useEffect(() => {
    eingehaengt = halter;
    melden();
    return () => {
      if (eingehaengt === halter) {
        eingehaengt = null;
        melden();
      }
    };
  }, []);
}

/* ══════════════ Die rollende Fläche unter dem Finger ══════════════ */

/**
 * Welche Fläche der Zug bewegt.
 *
 * Gesucht wird von der Berührung nach oben bis zum Arbeitsbereich. Die
 * **innerste, die senkrecht wirklich rollt**, gewinnt — das ist die Liste,
 * in der gerade gewischt wird. Rollt keine (kurzer Schirm, der ganz ins
 * Bild passt), nimmt die **äußerste** Kandidatin den Zug: `.handy` bzw.
 * `.schirme`, also die Fläche der Maske selbst.
 *
 * Der Umweg über „äußerste" ist kein Feinschliff: Eine Tabelle, die nur zur
 * Seite rollt, rechnet `overflow-y` auf `auto` hoch, ohne je senkrecht zu
 * rollen. Ohne diese Unterscheidung schöbe der Zug die Tabelle nach unten
 * statt die Maske.
 */
function rollerVon(start: Element, grenze: HTMLElement): HTMLElement | null {
  let aeusserste: HTMLElement | null = null;
  let el: Element | null = start;
  while (el && el !== grenze) {
    if (el instanceof HTMLElement) {
      const y = getComputedStyle(el).overflowY;
      if (y === "auto" || y === "scroll") {
        if (el.scrollHeight > el.clientHeight) return el;
        aeusserste = el;
      }
    }
    el = el.parentElement;
  }
  return aeusserste;
}

/** Was der Schirm gerade sagt. */
type Lage = "ruhig" | "zieht" | "reicht" | "laeuft" | "fertig" | "ohne-netz" | "ging-nicht";

/**
 * Wann zuletzt etwas vom Server kam.
 *
 * Genommen wird der **jüngste** Stand der aktiven Abfragen — das ist, was
 * ein Mensch mit „zuletzt aktualisiert" meint. Der älteste wäre die
 * ehrlichere Zahl für „wie alt ist das Bild insgesamt", aber er stünde
 * nach jedem Maskenwechsel auf einer Abfrage, die im Hintergrund hängt,
 * und läse sich wie ein Fehler.
 */
function zuletztGeholt(kunde: QueryClient): number | null {
  let jung = 0;
  for (const a of kunde.getQueryCache().findAll({ type: "active" })) {
    if (a.state.dataUpdatedAt > jung) jung = a.state.dataUpdatedAt;
  }
  return jung > 0 ? jung : null;
}

/** Steht auf einer der aktiven Abfragen ein Fehler? */
function etwasGingNicht(kunde: QueryClient): boolean {
  return kunde.getQueryCache().findAll({ type: "active" })
    .some((a) => a.state.status === "error");
}

function ruhigeGangart(): boolean {
  return typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

const warte = (ms: number) => new Promise<void>((fertig) => { setTimeout(fertig, ms); });

/* ══════════════════════════════════════════════════════════════════ */

type Zug = {
  zeiger: number;
  startX: number;
  startY: number;
  zieht: boolean;
  weg: number;
};

/**
 * Das Band oben und die Geste dazu — **einmal in der Schale**, nie in einer
 * Maske. Steht keine Maske eingehängt, ist hier nichts: kein Element, kein
 * Horcher, kein Bild.
 */
export function Ziehauffrischen() {
  const t = useT();
  const kunde = useQueryClient();
  const telefon = useTelefon();
  const halter = useSyncExternalStore(abonnieren, standJetzt, amServer);
  const wartende = useWarteschlange();

  const [lage, setzeLage] = useState<Lage>("ruhig");
  const band = useRef<HTMLDivElement>(null);
  const kreisel = useRef<HTMLSpanElement>(null);
  const zug = useRef<Zug | null>(null);
  /* Die rollende Fläche steht **neben** dem Zug und nicht darin: Sie wird
     noch gebraucht, wenn die Geste längst vorbei ist und das Band oben
     hängt. Erst wenn alles wieder an seinem Platz ist, wird sie losgelassen. */
  const flaeche = useRef<HTMLElement | null>(null);
  const laeuft = useRef(false);
  const nachklang = useRef<number | undefined>(undefined);

  /* Am Schreibtisch und im Telefonbrowser gibt es die Geste nicht. Beides
     steht in `schale/geraet.ts`; `useTelefon` zeichnet neu, wenn jemand
     dreht, und `istInstalliert` wird dabei mitgelesen. */
  const gilt = telefon && istInstalliert() && halter !== null;

  /** Das Band und die Fläche darunter auf ihren Platz setzen. */
  const setzen = useCallback((weg: number, weich: boolean) => {
    const dauer = weich && !ruhigeGangart() ? "transform 220ms ease-out" : "none";
    const f = flaeche.current;
    if (f) {
      f.style.transition = dauer;
      f.style.transform = weg > 0 ? `translateY(${weg}px)` : "";
    }
    const b = band.current;
    if (b) {
      b.style.transition = dauer;
      b.style.transform = bandLage(Math.min(weg, HOEHE));
    }
    /* Der Kreisel dreht sich mit dem Zug — eine volle Umdrehung bis zur
       Schwelle. Das ist kein Schmuck: Er sagt, wie weit es noch ist,
       bevor das Wort es sagt. */
    const k = kreisel.current;
    if (k && !laeuft.current) {
      k.style.transform = `rotate(${Math.round((weg / AUSLOESEN) * 360)}deg)`;
    }
  }, []);

  /** Das Band allein — ohne die Fläche darunter, die schon zurück ist. */
  const bandSetzen = useCallback((oben: boolean) => {
    const b = band.current;
    if (!b) return;
    b.style.transition = ruhigeGangart() ? "none" : "transform 220ms ease-out";
    b.style.transform = bandLage(oben ? HOEHE : 0);
  }, []);

  /** Die Fläche loslassen: kein Umformen, kein Übergang, keine Spur. */
  const flaecheFreigeben = useCallback(() => {
    const f = flaeche.current;
    if (!f) return;
    f.style.transition = "";
    f.style.transform = "";
    flaeche.current = null;
  }, []);

  /* ── Holen ──────────────────────────────────────────────────────── */

  const holen = useCallback(async () => {
    if (laeuft.current) return;
    laeuft.current = true;
    window.clearTimeout(nachklang.current);
    setzeLage("laeuft");
    // Während geholt wird, steht das Band still im Bild und die Liste
    // darunter bleibt, wo der Finger sie gelassen hat.
    setzen(HOEHE, true);

    let ausgang: Lage = "fertig";
    try {
      const arbeit = (async () => {
        /* Erst hinaus, dann herein (siehe oben). `uebertragen` kehrt ohne
           Netz sofort zurück und lässt die Warteschlange stehen. */
        await uebertragen();
        await kunde.refetchQueries({ type: "active" });
        if (eingehaengt?.current) await eingehaengt.current();
      })();
      await Promise.all([arbeit, warte(MINDESTENS)]);
      if (ohneNetz()) ausgang = "ohne-netz";
      else if (etwasGingNicht(kunde)) ausgang = "ging-nicht";
    } catch {
      ausgang = "ging-nicht";
    }

    laeuft.current = false;
    setzeLage(ausgang);
    /* Die Liste fährt sofort zurück — sie ist fertig. Das Ergebnis bleibt
       als Band darüber stehen, bis es gelesen ist. */
    setzen(0, true);
    bandSetzen(true);
    nachklang.current = window.setTimeout(() => {
      bandSetzen(false);
      setzeLage("ruhig");
      flaecheFreigeben();
    }, NACHKLANG);
  }, [kunde, setzen, bandSetzen, flaecheFreigeben]);

  /* ── Die Geste ──────────────────────────────────────────────────── */

  useEffect(() => {
    if (!gilt) return undefined;
    const raum = document.getElementById(RAUM);
    if (!raum) return undefined;

    const anfassen = (e: TouchEvent) => {
      if (laeuft.current || zug.current || e.touches.length !== 1) return;
      const finger = e.touches[0];
      const ziel = e.target;
      if (!finger || !(ziel instanceof Element)) return;
      // Ein Blatt oder ein Deckblatt behält die Bewegung für sich — ob die
      // Berührung darin beginnt oder ob überhaupt eines offen steht.
      if (ziel.closest(DECKT) || raum.querySelector(DECKT)) return;
      // Die Leiste unten und die schwebenden Knöpfe rollen nicht (oben, `NICHT_HIER`).
      if (ziel.closest(NICHT_HIER)) return;
      const roller = rollerVon(ziel, raum);
      if (roller && roller.scrollTop > 0) return;
      flaeche.current = roller;
      zug.current = {
        zeiger: finger.identifier,
        startX: finger.clientX,
        startY: finger.clientY,
        zieht: false,
        weg: 0,
      };
    };

    const ziehen = (e: TouchEvent) => {
      const z = zug.current;
      if (!z) return;
      const finger = Array.from(e.touches).find((f) => f.identifier === z.zeiger);
      if (!finger) return;
      const hoch = finger.clientY - z.startY;
      const quer = Math.abs(finger.clientX - z.startX);

      if (!z.zieht) {
        // Nach oben, zur Seite oder ein zweiter Finger: gehört uns nicht.
        if (e.touches.length > 1 || hoch < -SCHWELLE || quer > SCHWELLE * 2) {
          zug.current = null;
          flaecheFreigeben();
          return;
        }
        if (hoch < SCHWELLE || quer > hoch) return;
        // Zwischendurch könnte die Liste gerollt sein — dann gilt sie.
        if (flaeche.current && flaeche.current.scrollTop > 0) {
          zug.current = null;
          flaecheFreigeben();
          return;
        }
        z.zieht = true;
        setzeLage("zieht");
      }

      /* Ab hier gehört die Bewegung uns. Ohne das spannt Safari sein
         Gummiband und Chrome fängt sein eigenes Überziehen an. */
      if (e.cancelable) e.preventDefault();
      const weg = widerstand(hoch - SCHWELLE);
      const reichteVorher = z.weg >= AUSLOESEN;
      const reichtJetzt = weg >= AUSLOESEN;
      z.weg = weg;
      setzen(weg, false);
      if (reichtJetzt !== reichteVorher) setzeLage(reichtJetzt ? "reicht" : "zieht");
    };

    const loslassen = () => {
      const z = zug.current;
      if (!z) return;
      zug.current = null;
      if (!z.zieht) {
        flaecheFreigeben();
        return;
      }
      /* Weit genug: Das Holen übernimmt von hier — es hält die Fläche
         unten, bis es fertig ist, und gibt sie danach frei. */
      if (z.weg >= AUSLOESEN) {
        void holen();
        return;
      }
      // Zu wenig: Es federt zurück, und danach ist nichts gewesen.
      setzen(0, true);
      setzeLage("ruhig");
      window.setTimeout(flaecheFreigeben, 260);
    };

    const abbrechen = () => {
      const z = zug.current;
      if (!z) return;
      zug.current = null;
      if (z.zieht) {
        setzen(0, true);
        setzeLage("ruhig");
        window.setTimeout(flaecheFreigeben, 260);
        return;
      }
      flaecheFreigeben();
    };

    raum.addEventListener("touchstart", anfassen, { passive: true });
    raum.addEventListener("touchmove", ziehen, { passive: false });
    raum.addEventListener("touchend", loslassen);
    raum.addEventListener("touchcancel", abbrechen);
    return () => {
      raum.removeEventListener("touchstart", anfassen);
      raum.removeEventListener("touchmove", ziehen);
      raum.removeEventListener("touchend", loslassen);
      raum.removeEventListener("touchcancel", abbrechen);
    };
  }, [gilt, holen, setzen, flaecheFreigeben]);

  /* Beim Maskenwechsel bleibt nichts hängen: Wer während des Holens
     weitergeht, fände sonst eine Fläche vor, die 58 px zu tief steht. */
  useEffect(() => () => {
    window.clearTimeout(nachklang.current);
    const f = flaeche.current;
    if (f) {
      f.style.transition = "";
      f.style.transform = "";
    }
  }, []);

  if (!gilt) return null;

  const geholt = lage === "ruhig" ? null : zuletztGeholt(kunde);
  const stumm = lage === "ruhig";

  const wort = lage === "reicht" ? "ziehauffrischen.loslassen"
    : lage === "laeuft" ? "ziehauffrischen.laeuft"
      : lage === "fertig" ? "ziehauffrischen.fertig"
        : lage === "ohne-netz" ? "ziehauffrischen.ohneNetz"
          : lage === "ging-nicht" ? "ziehauffrischen.gingNicht"
            : "ziehauffrischen.ziehen";

  /* Die zweite Zeile ist die Auskunft, die der Kreisel nicht geben kann:
     **wann zuletzt** — und wenn nichts ging, **was liegen bleibt**. */
  const unter = lage === "ohne-netz" && wartende.length
    ? t(wartende.length === 1 ? "ziehauffrischen.wartenEiner" : "ziehauffrischen.wartenMehrere",
      { zahl: wartende.length })
    : lage === "ohne-netz" || lage === "ging-nicht" || lage === "zieht" || lage === "reicht"
      ? (geholt === null ? "" : t("ziehauffrischen.zuletzt", { uhr: uhr(geholt) }))
      : "";

  return (
    <div
      className={`zauf zauf-${lage}`}
      ref={band}
      role="status"
      aria-live="polite"
      {...(stumm ? { "aria-hidden": true as const } : {})}
    >
      <span className="zauf-kreisel" ref={kreisel} aria-hidden="true" />
      <span className="zauf-worte">
        <b>{t(wort, { uhr: uhr(lage === "fertig" ? (geholt ?? Date.now()) : Date.now()) })}</b>
        {unter ? <span className="zauf-leise">{unter}</span> : null}
      </span>
    </div>
  );
}
